import { useMap } from '@vis.gl/react-google-maps'
import { get } from 'lodash'
import PropTypes from 'prop-types'
import { useEffect, useRef, useCallback } from 'react'

import { filterValidCoordPairs } from 'components/common/map/constants'
import { fitWhenMapReady } from 'components/common/map/fitWhenMapReady'
import { getGoogleMapsBounds } from 'utils/map'

const BOX_SELECT_MODIFIER = 'Shift'

const SELECT_BOX_STYLE = { border: '2px dashed #1976D2', backgroundColor: 'rgba(25, 118, 210, 0.1)' }
const DESELECT_BOX_STYLE = { border: '2px dashed #d32f2f', backgroundColor: 'rgba(211, 47, 47, 0.1)' }

/**
 * MapController is rendered as a child of <Map> and provides:
 * 1. A compatibility proxy on mapRef.current (for ZoomToStopButton which calls jumpTo/flyTo)
 * 2. fitBounds when the `allCoordinates` or `fitBounds` prop changes
 * 3. Shift+drag box selection / Ctrl+Shift+drag box deselection of stop markers
 */
const MapController = ({
  mapRef,
  allCoordinates,
  fitBounds,
  onFitBoundsDone,
  onManualFitBounds,
  markers,
  onSelectMarkers,
  onDeselectMarkers,
  enableBoxSelect,
}) => {
  const googleMap = useMap()
  const isShiftHeld = useRef(false)
  const isCtrlHeld = useRef(false)
  const boxSelectStart = useRef(null)
  const selectionBoxEl = useRef(null)
  const hasUserInteracted = useRef(false)

  // Set up proxy on mapRef.current for backward-compatible ZoomToStopButton
  useEffect(() => {
    if (!googleMap || !mapRef) return undefined
    const panAndZoom = ({ center, zoom }) => {
      const [lng, lat] = center
      googleMap.setCenter({ lat: parseFloat(lat), lng: parseFloat(lng) })
      if (zoom !== undefined) googleMap.setZoom(zoom)
    }
    // eslint-disable-next-line no-param-reassign
    mapRef.current = {
      jumpTo: panAndZoom,
      flyTo: panAndZoom,
      easeTo: panAndZoom,
      panTo: ({ lat, lng }) => googleMap.setCenter({ lat, lng }),
      setZoom: zoom => googleMap.setZoom(zoom),
      getMap: () => googleMap,
      // Google-native bounds fit for [lng, lat] coordinate arrays — the Mapbox
      // fitBounds(bounds) signature has no equivalent on this proxy.
      fitCoordinates: (coordinates, { padding = 40, maxZoom } = {}) => {
        if (!Array.isArray(coordinates) || coordinates.length === 0) return
        const bounds = getGoogleMapsBounds(coordinates)
        if (!bounds) return
        googleMap.fitBounds(bounds, padding)
        if (maxZoom) {
          window.google.maps.event.addListenerOnce(googleMap, 'idle', () => {
            if (googleMap.getZoom() > maxZoom) googleMap.setZoom(maxZoom)
          })
        }
      },
    }
    return undefined
  }, [googleMap, mapRef])

  // Track user interaction (zoom/drag) to suppress auto-fitBounds
  useEffect(() => {
    if (!googleMap) return undefined
    const markInteracted = () => {
      hasUserInteracted.current = true
    }
    const dragListener = googleMap.addListener('dragstart', markInteracted)
    const zoomListener = googleMap.addListener('zoom_changed', markInteracted)
    return () => {
      dragListener.remove()
      zoomListener.remove()
    }
  }, [googleMap])

  // Expose manual fitBounds trigger
  const doFitBounds = useCallback(() => {
    if (!googleMap || !Array.isArray(allCoordinates)) return
    const finiteCoords = filterValidCoordPairs(allCoordinates)
    if (finiteCoords.length === 0) return
    if (finiteCoords.length === 1) {
      const [lng, lat] = finiteCoords[0]
      googleMap.panTo({ lat, lng })
      googleMap.setZoom(14)
    } else {
      const bounds = getGoogleMapsBounds(finiteCoords)
      if (bounds) googleMap.fitBounds(bounds, 40)
    }
    hasUserInteracted.current = false
  }, [googleMap, allCoordinates])

  useEffect(() => {
    if (onManualFitBounds) onManualFitBounds(doFitBounds)
  }, [doFitBounds, onManualFitBounds])

  // fitBounds effect — skip if user has already zoomed/panned
  useEffect(() => {
    if (!googleMap || !fitBounds || !Array.isArray(allCoordinates) || allCoordinates.length === 0) return undefined
    if (hasUserInteracted.current) {
      if (onFitBoundsDone) onFitBoundsDone()
      return undefined
    }

    return fitWhenMapReady(googleMap, () => {
      doFitBounds()
      if (onFitBoundsDone) onFitBoundsDone()
    })
  }, [googleMap, fitBounds, allCoordinates, onFitBoundsDone, doFitBounds])

  // Box selection
  const getLatLngFromPoint = useCallback(
    (x, y) => {
      if (!googleMap) return null
      const container = googleMap.getDiv()
      if (!container) return null
      const rect = container.getBoundingClientRect()
      const projection = googleMap.getProjection()
      if (!projection) return null
      const scale = 2 ** googleMap.getZoom()
      const worldPoint = projection.fromLatLngToPoint(googleMap.getCenter())
      const newPoint = new window.google.maps.Point(
        worldPoint.x - (container.offsetWidth / 2 - (x - rect.left)) / scale,
        worldPoint.y - (container.offsetHeight / 2 - (y - rect.top)) / scale
      )
      return projection.fromPointToLatLng(newPoint)
    },
    [googleMap]
  )

  useEffect(() => {
    if (!enableBoxSelect || !googleMap) return undefined

    const container = googleMap.getDiv()
    if (!container) return undefined

    const handleKeyDown = e => {
      if (e.key === 'Control' || e.key === 'Meta') isCtrlHeld.current = true
      if (e.key !== BOX_SELECT_MODIFIER) return
      isShiftHeld.current = true
      container.style.cursor = 'crosshair'
      googleMap.setOptions({ draggable: false, gestureHandling: 'none' })
    }

    const handleKeyUp = e => {
      if (e.key === 'Control' || e.key === 'Meta') isCtrlHeld.current = false
      if (e.key !== BOX_SELECT_MODIFIER) return
      isShiftHeld.current = false
      container.style.cursor = ''
      googleMap.setOptions({ draggable: true, gestureHandling: 'greedy' })
    }

    const handleMouseMove = e => {
      if (!boxSelectStart.current || !selectionBoxEl.current) return
      const { left, top } = container.getBoundingClientRect()
      const { x: startX, y: startY } = boxSelectStart.current

      // Update mode mid-drag based on current modifier keys
      const isDeselect = e.ctrlKey || e.metaKey
      boxSelectStart.current.isDeselect = isDeselect
      const boxStyle = isDeselect ? DESELECT_BOX_STYLE : SELECT_BOX_STYLE

      Object.assign(selectionBoxEl.current.style, {
        left: `${Math.min(startX, e.clientX) - left}px`,
        top: `${Math.min(startY, e.clientY) - top}px`,
        width: `${Math.abs(e.clientX - startX)}px`,
        height: `${Math.abs(e.clientY - startY)}px`,
        border: boxStyle.border,
        backgroundColor: boxStyle.backgroundColor,
      })
    }

    const filterMarkersInBounds = e => {
      const latLng1 = getLatLngFromPoint(boxSelectStart.current.x, boxSelectStart.current.y)
      const latLng2 = getLatLngFromPoint(e.clientX, e.clientY)
      if (!latLng1 || !latLng2 || !markers) return []
      const minLat = Math.min(latLng1.lat(), latLng2.lat())
      const maxLat = Math.max(latLng1.lat(), latLng2.lat())
      const minLng = Math.min(latLng1.lng(), latLng2.lng())
      const maxLng = Math.max(latLng1.lng(), latLng2.lng())
      return markers.filter(marker => {
        const coords = get(marker, 'geometry.coordinates', [])
        if (coords.length < 2) return false
        const markerLat = parseFloat(coords[1])
        const markerLng = parseFloat(coords[0])
        return markerLat >= minLat && markerLat <= maxLat && markerLng >= minLng && markerLng <= maxLng
      })
    }

    const handleMouseUp = e => {
      if (!boxSelectStart.current) return
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('mouseup', handleMouseUp)

      // Use the modifier state at release time (already synced during drag)
      const isDeselect = e.ctrlKey || e.metaKey || boxSelectStart.current.isDeselect
      const filtered = filterMarkersInBounds(e)

      if (filtered.length > 0) {
        if (isDeselect && onDeselectMarkers) {
          onDeselectMarkers(filtered)
        } else if (!isDeselect && onSelectMarkers) {
          onSelectMarkers(filtered)
        }
      }

      if (selectionBoxEl.current) {
        selectionBoxEl.current.remove()
        selectionBoxEl.current = null
      }
      boxSelectStart.current = null
    }

    const handleMouseDown = e => {
      if (!e.shiftKey && !isShiftHeld.current) return
      e.preventDefault()
      e.stopPropagation()

      isShiftHeld.current = true
      isCtrlHeld.current = e.ctrlKey || e.metaKey
      googleMap.setOptions({ draggable: false, gestureHandling: 'none' })

      const isDeselect = e.ctrlKey || e.metaKey
      const boxStyle = isDeselect ? DESELECT_BOX_STYLE : SELECT_BOX_STYLE

      boxSelectStart.current = { x: e.clientX, y: e.clientY, isDeselect }
      const box = document.createElement('div')
      Object.assign(box.style, {
        position: 'absolute',
        ...boxStyle,
        pointerEvents: 'none',
        zIndex: '1000',
      })
      container.style.position = 'relative'
      container.appendChild(box)
      selectionBoxEl.current = box
      window.addEventListener('mousemove', handleMouseMove)
      window.addEventListener('mouseup', handleMouseUp)
    }

    window.addEventListener('keydown', handleKeyDown)
    window.addEventListener('keyup', handleKeyUp)
    container.addEventListener('mousedown', handleMouseDown)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      window.removeEventListener('keyup', handleKeyUp)
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('mouseup', handleMouseUp)
      container.removeEventListener('mousedown', handleMouseDown)
      googleMap.setOptions({ draggable: true, gestureHandling: 'greedy' })
      if (selectionBoxEl.current) {
        selectionBoxEl.current.remove()
        selectionBoxEl.current = null
      }
    }
  }, [enableBoxSelect, googleMap, markers, onSelectMarkers, onDeselectMarkers, getLatLngFromPoint])

  return null
}

MapController.propTypes = {
  mapRef: PropTypes.shape({ current: PropTypes.object }),
  allCoordinates: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.number)),
  fitBounds: PropTypes.bool,
  onFitBoundsDone: PropTypes.func,
  onManualFitBounds: PropTypes.func,
  markers: PropTypes.array,
  onSelectMarkers: PropTypes.func,
  onDeselectMarkers: PropTypes.func,
  enableBoxSelect: PropTypes.bool,
}

export default MapController
